import React, { createContext, useMemo, useReducer, useCallback, useRef, useEffect } from 'react';
import type { SessionState, ChatMessage } from '../types/session.types';
import { sessionReducer, initialState } from './sessionReducer';
import { useAuth } from '../hooks/useAuth';
import { useSessionSSE } from '../hooks/useSessionSSE';
import * as sessionApi from '../api/session.api';
import { logger } from '../utils/logger';

export interface SessionContextType {
  activeSessions: Map<string, SessionState>;
  currentSessionId: string | null;
  currentSession: SessionState | null;
  canCreateSession: boolean;

  createSession: (agentName: string, fileIds?: string[]) => Promise<string>;
  switchSession: (sessionId: string) => Promise<void>;
  terminateSession: (sessionId: string) => Promise<void>;
  terminateAllSessions: () => Promise<void>;

  sendMessage: (sessionId: string, content: string, historyRefs?: string[], vaultFileRefs?: string[]) => Promise<void>;
  stopGeneration: (sessionId: string) => Promise<void>;
  resetSession: (sessionId: string) => Promise<void>;
  uploadFiles: (sessionId: string, files: File[], onProgress?: (pct: number) => void) => Promise<void>;
  stageVaultFiles: (sessionId: string, vaultKeys: string[]) => Promise<void>;
  deleteFile: (sessionId: string, fileId: string) => Promise<void>;
  /** Permanently purges a file from the S3 vault via DELETE ?flush=true. */
  flushFile: (sessionId: string, fileId: string) => Promise<void>;

  respondToPermission: (permissionId: string, approved: boolean) => Promise<void>;
  respondToQuestion: (questionId: string, answer: string) => Promise<void>;
  reconnectSSE: () => void;
  dispatch: React.Dispatch<any>;
}

export const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(sessionReducer, initialState);
  const { user, org } = useAuth();
  const historyLoadingRef = useRef<Set<string>>(new Set());

  // Lifecycle cleanup via sendBeacon
  useEffect(() => {
    const handleBeforeUnload = () => {
      const txApiBase = import.meta.env.VITE_TX_BFF_URL || '/tx-api';
      state.activeSessions.forEach((_, sessionId) => {
        const beaconUrl = `${txApiBase}/api/sessions/${sessionId}/disconnect`;
        navigator.sendBeacon(beaconUrl, JSON.stringify({ timestamp: new Date().toISOString() }));
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.activeSessions]);

  const currentSessionId = state.currentSessionId;
  const activeSessionIds = useMemo(() => Array.from(state.activeSessions.keys()), [state.activeSessions]);
  const { connect: connectSSE, disconnect: disconnectSSE, reconnect: reconnectSSEHook } = useSessionSSE(activeSessionIds, dispatch);

  const canCreateSession = state.activeSessions.size < 3;

  const currentSession = useMemo(() => {
    return state.currentSessionId ? state.activeSessions.get(state.currentSessionId) ?? null : null;
  }, [state.currentSessionId, state.activeSessions]);

  const createSession = useCallback(async (agentName: string, fileIds?: string[]) => {
    if (!user || !org) {
      throw new Error('User or Organization not found in context');
    }
    if (!canCreateSession) {
      throw new Error('Maximum concurrent sessions reached');
    }

    try {
      const response = await sessionApi.createSession({
        agentName,
        userId: user.uid,
        orgId: org.org_id,
        fileIds,
      });


      dispatch({
        type: 'CREATE_SESSION',
        payload: {
          sessionId: response.sessionId,
          agentName,
          maxTurns: response.maxTurns,
          entitlementSnapshot: {
            isFileUploadEnabled: response.isFileUploadEnabled ?? true,
            isS3FlushEnabled: response.isS3FlushEnabled ?? response.isFileUploadEnabled ?? false,
            fileRetentionTtlHours: response.tempDataTtlHours ?? 24,
          },
        },
      });

      return response.sessionId;
    } catch (error) {
      logger.error('Failed to create session', error);
      throw error;
    }
  }, [user, org, canCreateSession]);

  const switchSession = useCallback(async (sessionId: string) => {
    if (!sessionId || sessionId === 'undefined') {
      logger.warn('Attempted to switch to invalid sessionId', { sessionId });
      return;
    }
    dispatch({ type: 'SWITCH_SESSION', payload: { sessionId } });

    // Fetch history if session has no messages and not already loading
    const targetSession = state.activeSessions.get(sessionId);
    if (targetSession && targetSession.messages.length === 0 && !historyLoadingRef.current.has(sessionId)) {
      historyLoadingRef.current.add(sessionId);
      try {
        const messages = await sessionApi.getHistory(sessionId);
        dispatch({ type: 'SET_MESSAGES', payload: { sessionId, messages } });
      } catch (error) {
        logger.error(`Failed to fetch history for session ${sessionId}`, error);
      } finally {
        historyLoadingRef.current.delete(sessionId);
      }
    }
  }, [state.activeSessions]);

  const terminateSession = useCallback(async (sessionId: string) => {
    try {
      await sessionApi.terminateSession(sessionId);
      dispatch({ type: 'TERMINATE_SESSION', payload: { sessionId } });
    } catch (error) {
      logger.error(`Failed to terminate session ${sessionId}`, error);
      throw error;
    }
  }, []);

  const terminateAllSessions = useCallback(async () => {
    try {
      const promises = Array.from(state.activeSessions.keys()).map(id => sessionApi.terminateSession(id));
      await Promise.all(promises);
      dispatch({ type: 'TERMINATE_ALL' });
    } catch (error) {
      logger.error('Failed to terminate all sessions', error);
      throw error;
    }
  }, [state.activeSessions]);

  const sendMessage = useCallback(async (sessionId: string, content: string, historyRefs?: string[], vaultFileRefs?: string[]) => {
    try {
      const targetSession = state.activeSessions.get(sessionId);
      const turnIndex = targetSession ? targetSession.messages.length : 0;
      dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          sessionId,
          message: {
            id: `usr-${Date.now()}`,
            role: 'user',
            content,
            timestamp: new Date().toISOString(),
            turnIndex,
          },
        },
      });

      const hasHistory = historyRefs && historyRefs.length > 0;
      const hasVault = vaultFileRefs && vaultFileRefs.length > 0;
      if (hasHistory || hasVault) {
        await sessionApi.sendMessage(sessionId, content, historyRefs, vaultFileRefs);
      } else {
        await sessionApi.sendMessage(sessionId, content);
      }
      connectSSE(sessionId);
    } catch (error: any) {
      if (error?.code === 'TURN_LIMIT_EXCEEDED') {
        dispatch({ type: 'UPDATE_STATUS', payload: { sessionId, status: 'complete' } });
        return;
      }
      logger.error(`Failed to send message to session ${sessionId}`, error);
      throw error;
    }
  }, [state.activeSessions, connectSSE]);

  const stopGeneration = useCallback(async (sessionId: string) => {
    try {
      await sessionApi.stopGeneration(sessionId);
      disconnectSSE(sessionId);
    } catch (error) {
      logger.error(`Failed to stop generation for session ${sessionId}`, error);
      throw error;
    }
  }, [disconnectSSE]);

  const resetSession = useCallback(async (sessionId: string) => {
    try {
      await sessionApi.resetSession(sessionId);
      dispatch({ type: 'RESET_SESSION', payload: { sessionId } });
    } catch (error) {
      logger.error(`Failed to reset session ${sessionId}`, error);
      throw error;
    }
  }, []);

  const uploadFiles = useCallback(async (sessionId: string, files: File[], onProgress?: (pct: number) => void) => {
    try {
      const result = await sessionApi.uploadSessionFiles(sessionId, files, onProgress);
      const uploadedFiles = result.files.map((file) => ({
        fileId: file.fileId,
        filename: file.filename,
        sizeBytes: file.sizeBytes,
        type: 'input' as const,
        createdAt: new Date().toISOString(),
      }));
      dispatch({ type: 'ADD_FILES', payload: { sessionId, files: uploadedFiles } });

      // Add a system message for feedback
      dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          sessionId,
          message: {
            id: `sys-${Date.now()}`,
            role: 'assistant',
            content: `I have received ${files.length} file(s) and they have been securely processed. How can I help you with them?`,
            timestamp: new Date().toISOString(),
            turnIndex: -1, // Use -1 for system messages that don't count towards the limit
          }
        }
      });
    } catch (error) {
      logger.error(`Failed to upload files for session ${sessionId}`, error);
      throw error;
    }
  }, []);

  const stageVaultFiles = useCallback(async (sessionId: string, vaultKeys: string[]) => {
    try {
      const result = await sessionApi.stageVaultFiles(sessionId, vaultKeys);
      const sessionFiles = result.files.map((file) => ({
        fileId: file.fileId,
        filename: file.filename,
        sizeBytes: file.sizeBytes,
        type: 'input' as const,
        createdAt: new Date().toISOString(),
      }));
      dispatch({ type: 'ADD_FILES', payload: { sessionId, files: sessionFiles } });

      // Add a system message for feedback
      dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          sessionId,
          message: {
            id: `sys-${Date.now()}`,
            role: 'assistant',
            content: `I have received ${vaultKeys.length} vault file(s) and they have been securely processed. How can I help you with them?`,
            timestamp: new Date().toISOString(),
            turnIndex: -1,
          }
        }
      });
    } catch (error) {
      logger.error(`Failed to stage vault files for session ${sessionId}`, error);
      throw error;
    }
  }, []);

  const deleteFile = useCallback(async (sessionId: string, fileId: string) => {
    try {
      await sessionApi.deleteFile(fileId);
      dispatch({ type: 'REMOVE_ARTIFACT', payload: { sessionId, fileId } });
    } catch (error) {
      logger.error(`Failed to delete file ${fileId} for session ${sessionId}`, error);
      throw error;
    }
  }, []);

  const flushFile = useCallback(async (sessionId: string, fileId: string) => {
    try {
      await sessionApi.flushS3File(fileId);
      dispatch({ type: 'REMOVE_ARTIFACT', payload: { sessionId, fileId } });
    } catch (error) {
      logger.error(`Failed to flush file ${fileId} from vault for session ${sessionId}`, error);
      throw error;
    }
  }, []);
  const respondToPermission = useCallback(async (permissionId: string, approved: boolean) => {
    if (!currentSessionId) return;
    try {
      await sessionApi.respondToPermission(permissionId, approved);
      dispatch({ type: 'SET_ACTIVE_PERMISSION', payload: { sessionId: currentSessionId, permission: null } });
      
      if (!approved) {
        // Show system message on denial
        const systemMsg: ChatMessage = {
          id: `sys-${Date.now()}`,
          role: 'assistant', // System messages use assistant role with specific styling or marker
          content: '_Operation cancelled by user._',
          timestamp: new Date().toISOString(),
          turnIndex: currentSession?.messages.length || 0,
        };
        dispatch({ type: 'ADD_MESSAGE', payload: { sessionId: currentSessionId, message: systemMsg } });
      }
    } catch (error) {
      logger.error(`Failed to respond to permission ${permissionId}`, error);
      throw error;
    }
  }, [currentSessionId, currentSession]);

  const respondToQuestion = useCallback(async (questionId: string, answer: string) => {
    if (!currentSessionId) return;
    try {
      await sessionApi.respondToQuestion(questionId, answer);
      dispatch({ type: 'SET_ACTIVE_QUESTION', payload: { sessionId: currentSessionId, question: null } });
    } catch (error) {
      logger.error(`Failed to respond to question ${questionId}`, error);
      throw error;
    }
  }, [currentSessionId]);

  const reconnectSSE = useCallback(() => {
    if (currentSessionId) {
      reconnectSSEHook(currentSessionId);
    }
  }, [currentSessionId, reconnectSSEHook]);

  const value = useMemo(() => ({
    activeSessions: state.activeSessions,
    currentSessionId: state.currentSessionId,
    currentSession,
    canCreateSession,
    createSession,
    switchSession,
    terminateSession,
    terminateAllSessions,
    sendMessage,
    stopGeneration,
    resetSession,
    uploadFiles,
    stageVaultFiles,
    deleteFile,
    flushFile,
    respondToPermission,
    respondToQuestion,
    reconnectSSE,
    dispatch,
  }), [
    state.activeSessions,
    state.currentSessionId,
    currentSession,
    canCreateSession,
    createSession,
    switchSession,
    terminateSession,
    terminateAllSessions,
    sendMessage,
    stopGeneration,
    resetSession,
    uploadFiles,
    stageVaultFiles,
    deleteFile,
    flushFile,
    respondToPermission,
    respondToQuestion,
    reconnectSSE,
    dispatch,
  ]);

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};
